import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { postNamedCommand, postNamedQuery } from '../../../../../../../service'

dayjs.extend(utc)

const createUserMessage = (threadId, prompt) => ({
  threadId,
  threadMessageId: null,
  actor: 'user',
  message: prompt,
  data: {},
  isGenerating: false,
  generationStatus: null,
  responseId: null,
  attachments: [],
  isPlaceholder: true,
  createdDate: dayjs.utc().toISOString()
})

const createAgentPlaceholderMessage = (threadId, responseId) => ({
  threadId,
  threadMessageId: responseId,
  actor: 'agent',
  message: '',
  data: {},
  isGenerating: true,
  generationStatus: 'Thinking...',
  responseId: null,
  attachments: [],
  isPlaceholder: true,
  createdDate: dayjs.utc().toISOString()
})

export const abundanceDesigner = {
  useInvokeAbundanceDesigner: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async ({ prompt, threadId, aeContext }) => {
        // Only set query cache if we have a threadId (not for first message)
        if (threadId) {
          queryClient.setQueryData(['admin/abundance-designer-thread', threadId], prev => {
            if (!prev) {
              return {
                thread: {
                  messages: [createUserMessage(threadId, prompt)]
                }
              }
            }
            prev.thread.messages.push(createUserMessage(threadId, prompt))

            return prev
          })
        }

        const { data } = await postNamedCommand('denali', 'invoke-abundance-designer', {
          ...(!!threadId && { threadId }),
          prompt,
          ...(aeContext && { aeContext })
        })

        return data
      },
      onSuccess: async (data, { prompt, threadId }) => {
        const newMessage = data.message
        // Use the threadId from the server response if no threadId was provided initially
        const effectiveThreadId = threadId || data.threadId || newMessage.threadId

        queryClient.setQueryData(['admin/abundance-designer-thread', effectiveThreadId], prev => {
          // If this was the first message (no prev data), initialize the thread
          if (!prev) {
            return {
              thread: {
                messages: [
                  newMessage,
                  createAgentPlaceholderMessage(effectiveThreadId, newMessage.responseId)
                ]
              }
            }
          }

          // For existing threads, replace placeholder message
          const placeholderIndex = prev.thread.messages.findIndex(m => m.actor === 'user' && m.isPlaceholder)
          prev.thread.messages[placeholderIndex] = newMessage
          prev.thread.messages.push(createAgentPlaceholderMessage(effectiveThreadId, newMessage.responseId))

          return prev
        })
      }
    })
  },

  useAbundanceDesignerThread: (threadId, options = {}) => {
    const { enabled = true, refetchInterval = null } = options
    return useQuery({
      queryKey: ['admin/abundance-designer-thread', threadId],
      queryFn: async () => {
        const { data } = await postNamedQuery('denali', 'get-abundance-designer-thread', {
          threadId
        })

        return data
      },
      enabled,
      refetchInterval,
      refetchOnWindowFocus: !!refetchInterval
    })
  },

  useGetAttachmentData: (threadId, threadMessageAttachmentId, options = {}) => {
    const { refetchInterval = null } = options
    return useQuery({
      queryKey: ['admin/get-abundance-designer-thread-attachment', threadId, threadMessageAttachmentId],
      queryFn: async () => {
        const { data } = await postNamedQuery('denali', 'get-abundance-designer-thread-attachment', {
          threadId,
          threadMessageAttachmentId
        })

        return data
      },
      enabled: !!(threadId && threadMessageAttachmentId),
      refetchInterval,
      refetchOnWindowFocus: !!refetchInterval
    })
  },

  useGenerationPolling: (message) => {
    const refetchInterval = useMemo(
      () => message?.isGenerating ? 5000 : undefined,
      [message?.isGenerating]
    )
    const queryClient = useQueryClient()

    const { mutateAsync, ...rest } = useMutation(
      async ({ message }) => {
        const { response, data } = await postNamedQuery(
          'denali',
          'get-abundance-designer-thread-message',
          {
            threadId: message.threadId,
            threadMessageId: message.threadMessageId
          }
        )

        if (response.status === 204) {
          return null
        }

        return data
      },
      {
        onSuccess (data, { message }) {
          if (!data?.message) return // case for 204
          const newMessage = data.message
          queryClient.setQueryData(
            ['admin/abundance-designer-thread', message.threadId],
            prev => {
              if (!prev) return prev
              const idx = prev.thread.messages.findIndex(m => m.threadMessageId === message.threadMessageId)
              if (idx > -1) {
                const msgs = [...prev.thread.messages]
                msgs[idx] = newMessage
                return {
                  ...prev,
                  thread: {
                    ...prev.thread,
                    messages: msgs
                  }
                }
              }
              return prev
            }
          )
        }
      }
    )

    useEffect(() => {
      if (!refetchInterval || !message) return
      mutateAsync({ message }).catch(console.error)
      const id = setInterval(() => {
        mutateAsync({ message }).catch(console.error)
      }, refetchInterval)
      return () => clearInterval(id)
    }, [
      message,
      message?.threadId,
      message?.threadMessageId,
      refetchInterval,
      mutateAsync
    ])

    return { mutateAsync, ...rest }
  }
}

// Admin-specific playground app hook that doesn't handle routing
export const useAdminPlaygroundApp = (initialThreadId, viewId, content) => {
  const [threadId, setThreadId] = useState(initialThreadId || null)
  const { data: thread, isLoading: isThreadLoading } = abundanceDesigner.useAbundanceDesignerThread(threadId, { enabled: !!threadId })

  const { mutateAsync: invokeAbundanceDesigner, isError: invokeError, isLoading: isInvokeLoading } = abundanceDesigner.useInvokeAbundanceDesigner()

  const addMessage = useCallback(async (message) => {
    // Build aeContext with viewId and current content if available
    const aeContext = {}
    if (viewId) {
      aeContext.viewId = viewId
    }
    if (content) {
      aeContext.content = content
    }

    // Send message with current threadId (could be undefined for first message)
    const result = await invokeAbundanceDesigner({
      threadId: threadId, // undefined for first message, server will generate
      prompt: message,
      ...(Object.keys(aeContext).length > 0 && { aeContext })
    })

    // If this was the first message (no threadId), set the threadId from server response
    if (!threadId && result?.threadId) {
      setThreadId(result.threadId)
    }

    return result
  }, [invokeAbundanceDesigner, threadId, viewId, content])

  const loadAttachmentData = useCallback(async (threadId, threadMessageAttachmentId) => {
    if (!threadId || !threadMessageAttachmentId) return null

    try {
      // Use React Query's prefetch to cache the data and then return it
      const { data } = await postNamedQuery('denali', 'get-abundance-designer-thread-attachment', {
        threadId,
        threadMessageAttachmentId
      })
      return data?.attachment
    } catch (error) {
      console.error('Error loading attachment data:', error)
      return null
    }
  }, [])

  return {
    addMessage,
    thread: thread?.thread,
    isThreadLoading: (threadId && isThreadLoading) || (!threadId && isInvokeLoading),
    isErrored: invokeError,
    loadAttachmentData,
    threadId
  }
}
